import { AfterViewInit, Component, effect, ElementRef, input, OnDestroy, output, signal, ViewChild } from '@angular/core'
import { CropperService } from '../../services/cropper.service'
import { Rect, ResizeHandle } from '../../types/cropper.type'

@Component({
  selector: 'cropper',
  standalone: true,
  imports: [],
  providers: [CropperService],
  templateUrl: './cropper.component.html',
  styleUrl: './cropper.component.scss'
})
export class CropperComponent implements AfterViewInit, OnDestroy {
  imageFile = input<File | string>()
  aspectRatio = input<number>(12 / 5)
  initialCropBox = input<Rect | null>(null)

  cropped = output<{ blob: Blob; cropBox: Rect }>()
  cancelled = output<void>()
  loadFailed = output<void>()
  /** Emitted when the image has finished loading and the cropper is ready to use */
  imageLoaded = output<void>()

  @ViewChild('canvas', { static: false }) canvasRef!: ElementRef<HTMLCanvasElement>
  @ViewChild('container', { static: false }) containerRef!: ElementRef<HTMLDivElement>

  private _ctx: CanvasRenderingContext2D | null = null
  private _image = signal<HTMLImageElement | null>(null)
  private _cropBox = signal<Rect>({ x: 0, y: 0, width: 0, height: 0 })
  private _isDragging = signal(false)
  private _isResizing = signal(false)
  private _dragStart = signal({ x: 0, y: 0 })
  private _resizeHandle = signal<ResizeHandle | ''>('')
  private _currentImageFile = signal<File | string | null>(null)
  // Image display area (centered and contained within canvas)
  private _imageDisplayArea = signal<Rect>({ x: 0, y: 0, width: 0, height: 0 })

  // High-DPI: logical size and device pixel ratio for crisp rendering when zoomed
  private _dpr = 1
  private _logicalWidth = 0
  private _logicalHeight = 0

  private readonly TARGET_BANNER_WIDTH = 1200
  private readonly TARGET_BANNER_HEIGHT = 500

  constructor(private _cropperService: CropperService) {
    effect(() => {
      const newImageFile = this.imageFile()
      const currentImageFile = this._currentImageFile()
      const isSame = currentImageFile === newImageFile
      if (newImageFile && !isSame) {
        this._currentImageFile.set(newImageFile)
        this._cropBox.set({ x: 0, y: 0, width: 0, height: 0 })
        setTimeout(() => this._loadImage(), 100)
      }
    })
  }

  ngAfterViewInit(): void {
    if (this.canvasRef?.nativeElement) this._ctx = this.canvasRef.nativeElement.getContext('2d')
  }

  ngOnDestroy(): void {
    this._image.set(null)
  }

  /**
   * Load the image into the canvas
   * @returns void
   */
  private _loadImage(): void {
    const fileOrUrl = this.imageFile()
    if (!fileOrUrl || !this._ctx) return

    const img = new Image()

    if (typeof fileOrUrl === 'string') {
      try {
        const urlOrigin = new URL(fileOrUrl, window.location.href).origin
        if (urlOrigin !== window.location.origin) {
          img.crossOrigin = 'anonymous'
        }
      } catch {
        img.crossOrigin = 'anonymous'
      }
    }
    // For File we use a data URL below (same-origin), so no crossOrigin needed

    img.onload = () => {
      this._image.set(img)
      requestAnimationFrame(() => {
        setTimeout(() => {
          this._setupCanvas()
          this._createDefaultCropBox()
          this._draw()
          this.imageLoaded.emit()
        }, 0)
      })
    }

    img.onerror = () => {
      this._image.set(null)
      this.loadFailed.emit()
    }

    if (typeof fileOrUrl === 'string') {
      const base = fileOrUrl.split('?')[0]
      img.src = `${base}?crop=${Date.now()}`
    } else {
      const reader = new FileReader()
      reader.onload = (e) => {
        if (e.target?.result) img.src = e.target.result as string
      }
      reader.onerror = () => {
        this._image.set(null)
        this.loadFailed.emit()
      }
      reader.readAsDataURL(fileOrUrl)
    }
  }

  /**
   * Setup the canvas
   * @returns void
   */
  private _setupCanvas(): void {
    if (!this.canvasRef?.nativeElement || !this._image() || !this.containerRef?.nativeElement) return

    const container = this.containerRef.nativeElement
    // Get actual container dimensions
    const containerRect = container.getBoundingClientRect()
    const containerWidth = containerRect.width || container.offsetWidth || 800
    const containerHeight = containerRect.height || container.offsetHeight || 600

    const canvas = this.canvasRef.nativeElement
    const img = this._image()!
    const imageAspect = img.width / img.height
    const containerAspect = containerWidth / containerHeight

    // High-DPI: use device pixel ratio so canvas stays sharp when zoomed or on Retina
    this._dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 3))
    this._logicalWidth = containerWidth
    this._logicalHeight = containerHeight
    canvas.width = Math.floor(containerWidth * this._dpr)
    canvas.height = Math.floor(containerHeight * this._dpr)

    // Display size stays at container size (CSS)
    canvas.style.width = '100%'
    canvas.style.height = '100%'

    // Calculate image display area (centered and contained with original aspect ratio)
    let displayWidth: number
    let displayHeight: number
    let displayX: number
    let displayY: number

    if (imageAspect > containerAspect) {
      // Image is wider relative to container - fit to container width
      displayWidth = containerWidth
      displayHeight = displayWidth / imageAspect
      displayX = 0
      displayY = (containerHeight - displayHeight) / 2
    } else {
      // Image is taller relative to container - fit to container height
      displayHeight = containerHeight
      displayWidth = displayHeight * imageAspect
      displayX = (containerWidth - displayWidth) / 2
      displayY = 0
    }

    // Store the image display area for drawing
    this._imageDisplayArea.set({
      x: displayX,
      y: displayY,
      width: displayWidth,
      height: displayHeight
    })
  }

  /**
   * Create the default crop box: largest rectangle with target aspect ratio
   * that fits inside the image display area, centered horizontally and vertically.
   */
  private _createDefaultCropBox(): void {
    if (!this.canvasRef?.nativeElement || !this._image()) return
    const imageArea = this._imageDisplayArea()
    const maxWidth = imageArea.width
    const maxHeight = imageArea.height
    const targetAspect = this.aspectRatio()

    let cropWidth: number
    let cropHeight: number

    if (maxWidth / maxHeight > targetAspect) {
      // Image area is wider than target aspect â€“ use full height, width from aspect
      cropHeight = maxHeight
      cropWidth = cropHeight * targetAspect
    } else {
      // Image area is taller than target aspect â€“ use full width, height from aspect
      cropWidth = maxWidth
      cropHeight = cropWidth / targetAspect
    }

    // Center within image display area
    const x = imageArea.x + (maxWidth - cropWidth) / 2
    const y = imageArea.y + (maxHeight - cropHeight) / 2

    this._cropBox.set({ x, y, width: cropWidth, height: cropHeight })
  }

  /**
   * Draw the crop box and the image on the canvas
   * @returns void
   */
  private _draw(): void {
    if (!this._ctx || !this._image() || !this.canvasRef?.nativeElement) return

    const canvas = this.canvasRef.nativeElement
    const box = this._cropBox()
    const img = this._image()!
    const imageArea = this._imageDisplayArea()

    // Scale context for high-DPI so we draw in logical pixels; backing store is dprÃ— larger
    this._ctx.setTransform(this._dpr, 0, 0, this._dpr, 0, 0)
    this._ctx.clearRect(0, 0, this._logicalWidth, this._logicalHeight)

    // Draw the image centered and contained with its original aspect ratio
    this._ctx.imageSmoothingEnabled = true
    this._ctx.imageSmoothingQuality = 'high'
    this._ctx.drawImage(
      img,
      0,
      0,
      img.width,
      img.height, // Source: full image
      imageArea.x,
      imageArea.y,
      imageArea.width,
      imageArea.height // Destination: centered and contained area
    )

    this._drawOverlay(canvas, box)
    this._drawBorder(box)
    this._drawCornerIndicators(box)
  }

  /**
   * Draw the overlay on the canvas (only within the image display area)
   * @param {HTMLCanvasElement} canvas - The canvas element
   * @param {Rect} box - The crop box
   * @returns void
   */
  private _drawOverlay(canvas: HTMLCanvasElement, box: Rect): void {
    const imageArea = this._imageDisplayArea()
    this._ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'

    // Calculate the intersection of crop box with image area
    const cropStartX = Math.max(box.x, imageArea.x)
    const cropStartY = Math.max(box.y, imageArea.y)
    const cropEndX = Math.min(box.x + box.width, imageArea.x + imageArea.width)
    const cropEndY = Math.min(box.y + box.height, imageArea.y + imageArea.height)

    // Top overlay (within image area)
    if (cropStartY > imageArea.y) {
      this._ctx.fillRect(imageArea.x, imageArea.y, imageArea.width, cropStartY - imageArea.y)
    }

    // Bottom overlay (within image area)
    if (cropEndY < imageArea.y + imageArea.height) {
      this._ctx.fillRect(imageArea.x, cropEndY, imageArea.width, imageArea.y + imageArea.height - cropEndY)
    }

    // Left overlay (within image area)
    if (cropStartX > imageArea.x) {
      this._ctx.fillRect(imageArea.x, cropStartY, cropStartX - imageArea.x, cropEndY - cropStartY)
    }

    // Right overlay (within image area)
    if (cropEndX < imageArea.x + imageArea.width) {
      this._ctx.fillRect(cropEndX, cropStartY, imageArea.x + imageArea.width - cropEndX, cropEndY - cropStartY)
    }
  }

  /**
   * Draw the border on the canvas
   * @param {Rect} box - The crop box
   * @returns void
   */
  private _drawBorder(box: Rect): void {
    this._ctx.strokeStyle = '#ffffff'
    this._ctx.lineWidth = 2
    this._ctx.lineCap = 'round'
    this._ctx.lineJoin = 'round'
    this._ctx.setLineDash([5, 5])
    this._ctx.strokeRect(box.x, box.y, box.width, box.height)
    this._ctx.setLineDash([])
  }

  /**
   * Draw the corner indicators on the canvas
   * @param {Rect} box - The crop box
   * @returns void
   */
  private _drawCornerIndicators(box: Rect): void {
    const cornerLength = 24
    const cornerThickness = 6

    this._ctx.strokeStyle = '#ffffff'
    this._ctx.lineWidth = cornerThickness
    this._ctx.lineCap = 'round'
    this._ctx.lineJoin = 'round'

    // Top-left
    this._drawLCorner(box.x, box.y, cornerLength, 'tl')
    // Top-right
    this._drawLCorner(box.x + box.width, box.y, cornerLength, 'tr')
    // Bottom-left
    this._drawLCorner(box.x, box.y + box.height, cornerLength, 'bl')
    // Bottom-right
    this._drawLCorner(box.x + box.width, box.y + box.height, cornerLength, 'br')
  }

  /**
   * Draw the left corner indicator
   * @param {number} x - The x coordinate
   * @param {number} y - The y coordinate
   * @param {number} length - The length of the corner indicator
   * @param {string} position - The position of the corner indicator
   * @returns void
   */
  private _drawLCorner(x: number, y: number, length: number, position: 'tl' | 'tr' | 'bl' | 'br'): void {
    this._ctx.beginPath()

    if (position === 'tl') {
      this._ctx.moveTo(x, y + length)
      this._ctx.lineTo(x, y)
      this._ctx.lineTo(x + length, y)
    } else if (position === 'tr') {
      this._ctx.moveTo(x - length, y)
      this._ctx.lineTo(x, y)
      this._ctx.lineTo(x, y + length)
    } else if (position === 'bl') {
      this._ctx.moveTo(x, y - length)
      this._ctx.lineTo(x, y)
      this._ctx.lineTo(x + length, y)
    } else {
      // br
      this._ctx.moveTo(x - length, y)
      this._ctx.lineTo(x, y)
      this._ctx.lineTo(x, y - length)
    }

    this._ctx.stroke()
  }

  /**
   * Handle pointer down (mouse or touch start) - centralized logic
   * @param {number} scaledX - The scaled x coordinate
   * @param {number} scaledY - The scaled y coordinate
   * @returns void
   */
  private _handlePointerDown(scaledX: number, scaledY: number): void {
    const imageArea = this._imageDisplayArea()

    // Only allow interaction within the image display area
    if (scaledX < imageArea.x || scaledX > imageArea.x + imageArea.width || scaledY < imageArea.y || scaledY > imageArea.y + imageArea.height) {
      return
    }

    const box = this._cropBox()

    const handle = this._cropperService.detectHandle(scaledX, scaledY, box)
    if (handle) {
      this._isResizing.set(true)
      this._resizeHandle.set(handle)
      this._dragStart.set({ x: scaledX, y: scaledY })
      return
    }

    if (this._cropperService.isPointInBox(scaledX, scaledY, box)) {
      this._isDragging.set(true)
      this._dragStart.set({ x: scaledX - box.x, y: scaledY - box.y })
    }
  }

  /**
   * Handle pointer move (mouse or touch move) - centralized logic
   * @param {number} scaledX - The scaled x coordinate
   * @param {number} scaledY - The scaled y coordinate
   * @param {boolean} updateCursor - Whether to update the cursor (mouse only)
   * @returns void
   */
  private _handlePointerMove(scaledX: number, scaledY: number, updateCursor: boolean = false): void {
    if (!this.canvasRef?.nativeElement) return

    const canvas = this.canvasRef.nativeElement

    if (updateCursor && !this._isDragging() && !this._isResizing()) {
      const cursor = this._cropperService.getCursor(scaledX, scaledY, this._cropBox())
      canvas.style.cursor = cursor
    }

    if (this._isDragging()) this._handleDrag(scaledX, scaledY, canvas)
    else if (this._isResizing()) this._handleResize(scaledX, scaledY, canvas)
  }

  /**
   * Handle pointer up (mouse or touch end) - centralized logic
   * @param {boolean} resetCursor - Whether to reset the cursor (mouse only)
   * @returns void
   */
  private _handlePointerUp(resetCursor: boolean = false): void {
    this._isDragging.set(false)
    this._isResizing.set(false)
    this._resizeHandle.set('')

    if (resetCursor && this.canvasRef?.nativeElement) {
      this.canvasRef.nativeElement.style.cursor = 'move'
    }
  }

  /**
   * Handle the mouse down event
   * @param {MouseEvent} event - The mouse event
   * @returns void
   */
  public onMouseDown(event: MouseEvent): void {
    if (!this.canvasRef?.nativeElement) return

    const canvas = this.canvasRef.nativeElement
    const { scaledX, scaledY } = this._cropperService.getScaledCoordinates(event, canvas)
    this._handlePointerDown(scaledX, scaledY)
  }

  /**
   * Handle the mouse move event
   * @param {MouseEvent} event - The mouse event
   * @returns void
   */
  public onMouseMove(event: MouseEvent): void {
    if (!this.canvasRef?.nativeElement) return

    const canvas = this.canvasRef.nativeElement
    const { scaledX, scaledY } = this._cropperService.getScaledCoordinates(event, canvas)
    this._handlePointerMove(scaledX, scaledY, true)
  }

  /**
   * Handle the touch start event
   * @param {TouchEvent} event - The touch event
   * @returns void
   */
  public onTouchStart(event: TouchEvent): void {
    event.preventDefault() // Prevent scrolling and zooming
    if (!this.canvasRef?.nativeElement) return

    const canvas = this.canvasRef.nativeElement
    const { scaledX, scaledY } = this._cropperService.getScaledCoordinates(event, canvas)
    this._handlePointerDown(scaledX, scaledY)
  }

  /**
   * Handle the touch move event
   * @param {TouchEvent} event - The touch event
   * @returns void
   */
  public onTouchMove(event: TouchEvent): void {
    event.preventDefault() // Prevent scrolling
    if (!this.canvasRef?.nativeElement) return

    const canvas = this.canvasRef.nativeElement
    const { scaledX, scaledY } = this._cropperService.getScaledCoordinates(event, canvas)
    this._handlePointerMove(scaledX, scaledY, false)
  }

  /**
   * Handle the touch end event
   * @returns void
   */
  public onTouchEnd(): void {
    this._handlePointerUp(false)
  }

  /**
   * Handle the mouse up event
   * @returns void
   */
  public onMouseUp(): void {
    this._handlePointerUp(true)
  }

  /**
   * Handle the drag event
   * @param {number} scaledX - The scaled x coordinate
   * @param {number} scaledY - The scaled y coordinate
   * @param {HTMLCanvasElement} canvas - The canvas
   * @returns void
   */
  private _handleDrag(scaledX: number, scaledY: number, canvas: HTMLCanvasElement): void {
    const box = this._cropBox()
    const dragStart = this._dragStart()
    const imageArea = this._imageDisplayArea()

    // Calculate new position based on drag offset
    // dragStart contains the offset from the click point to box origin
    let newX = scaledX - dragStart.x
    let newY = scaledY - dragStart.y

    // Constrain to image display area bounds
    newX = Math.max(imageArea.x, Math.min(newX, imageArea.x + imageArea.width - box.width))
    newY = Math.max(imageArea.y, Math.min(newY, imageArea.y + imageArea.height - box.height))

    this._cropBox.set({
      ...box,
      x: newX,
      y: newY
    })
    this._draw()
  }

  /**
   * Handle the resize event
   * @param {number} scaledX - The scaled x coordinate
   * @param {number} scaledY - The scaled y coordinate
   * @param {HTMLCanvasElement} canvas - The canvas
   * @returns void
   */
  private _handleResize(scaledX: number, scaledY: number, canvas: HTMLCanvasElement): void {
    const box = this._cropBox()
    const ratio = this.aspectRatio()
    const handle = this._resizeHandle()
    const imageArea = this._imageDisplayArea()

    if (!handle) return

    let newBox = this._cropperService.resizeCropBox(box, handle, scaledX, scaledY, ratio)

    // Create a virtual canvas with image area dimensions for constraint calculations
    const virtualCanvas = {
      width: imageArea.width,
      height: imageArea.height
    } as HTMLCanvasElement

    // Adjust crop box coordinates relative to image area for constraint calculation
    const relativeBox = {
      ...newBox,
      x: newBox.x - imageArea.x,
      y: newBox.y - imageArea.y
    }

    let constrainedBox = this._cropperService.constrainToCanvas(relativeBox, virtualCanvas, handle, ratio)

    // Convert back to canvas coordinates
    constrainedBox.x += imageArea.x
    constrainedBox.y += imageArea.y

    // Final constraint check to ensure it stays within image area
    constrainedBox.x = Math.max(imageArea.x, Math.min(constrainedBox.x, imageArea.x + imageArea.width - constrainedBox.width))
    constrainedBox.y = Math.max(imageArea.y, Math.min(constrainedBox.y, imageArea.y + imageArea.height - constrainedBox.height))

    this._cropBox.set(constrainedBox)
    this._draw()
  }

  /**
   * Confirm the crop
   * @returns void
   */
  public onConfirm(): void {
    const img = this._image()
    if (!img || !this.canvasRef?.nativeElement) return

    const canvas = this.canvasRef.nativeElement
    const box = this._cropBox()
    const cropBoxRelativeToOriginal = this._calculateCropBoxRelativeToOriginal(canvas, img, box)

    const croppedCanvas = this._createCroppedCanvas(img, cropBoxRelativeToOriginal)
    if (!croppedCanvas) return

    croppedCanvas.toBlob((blob) => {
      if (blob) {
        this.cropped.emit({ blob, cropBox: cropBoxRelativeToOriginal })
      }
    }, 'image/png')
  }

  /**
   * Calculate the crop box relative to the original image
   * @param {HTMLCanvasElement} canvas - The canvas
   * @param {HTMLImageElement} img - The image
   * @param {Rect} box - The crop box
   * @returns {Rect} - The crop box relative to the original image
   */
  private _calculateCropBoxRelativeToOriginal(canvas: HTMLCanvasElement, img: HTMLImageElement, box: Rect): Rect {
    const imageArea = this._imageDisplayArea()

    // Convert crop box from canvas coordinates to image display area coordinates
    const relativeBox = {
      x: box.x - imageArea.x,
      y: box.y - imageArea.y,
      width: box.width,
      height: box.height
    }

    // Scale from image display area to original image dimensions
    const scaleX = img.width / imageArea.width
    const scaleY = img.height / imageArea.height

    return {
      x: relativeBox.x * scaleX,
      y: relativeBox.y * scaleY,
      width: relativeBox.width * scaleX,
      height: relativeBox.height * scaleY
    }
  }

  /**
   * Create the cropped canvas
   * @param {HTMLImageElement} img - The image
   * @param {Rect} cropBox - The crop box
   * @returns {HTMLCanvasElement | null} - The cropped canvas
   */
  private _createCroppedCanvas(img: HTMLImageElement, cropBox: Rect): HTMLCanvasElement | null {
    const croppedCanvas = document.createElement('canvas')
    croppedCanvas.width = this.TARGET_BANNER_WIDTH
    croppedCanvas.height = this.TARGET_BANNER_HEIGHT
    const croppedCtx = croppedCanvas.getContext('2d')

    if (!croppedCtx) return null

    croppedCtx.drawImage(img, cropBox.x, cropBox.y, cropBox.width, cropBox.height, 0, 0, this.TARGET_BANNER_WIDTH, this.TARGET_BANNER_HEIGHT)

    return croppedCanvas
  }

  /**
   * Cancel the crop
   * @returns void
   */
  public onCancel(): void {
    this.cancelled.emit()
  }
}
